import Cookie from 'js-cookie';
import { createMUPImpression } from '@spotify-internal/event-definitions/es5/events/createMUPImpression';
import { createMUPAction } from '@spotify-internal/event-definitions/es5/events/createMUPAction';
import { PlanType } from 'lib/plans';
import { ChangeAccountContext } from 'lib/trackingEvents';

type PageType =
  | 'purchase_confirmation'
  | 'user_has_plan_hopped_error'
  | 'user_has_failed_reverification_error'
  | 'change_account'
  | 'reverification_success'
  | 'reverification_failure'
  | 'reverification_enter_address'
  | 'reverification_address_confirmed'
  | 'reverification_link_expired'
  | 'reverification_redirect'
  | 'progress_indicator_one'
  | 'progress_indicator_two'
  | 'address'
  | 'invite'
  | 'member'
  | 'home_hub'
  | 'how_to_invite_members'
  | 'migration_info'
  | 'join_confirmation'
  | 'transition_error'
  | 'address_autocomplete'
  | 'feature_comparison'
  | 'join_landing_page'
  | 'member_remove_page'
  | 'country_mismatch_error';

type ErrorPageType =
  | 'address_mismatch'
  | 'country_mismatch'
  | 'generic'
  | 'invite_has_expired'
  | 'join_own_plan'
  | 'partner_transition'
  | 'plan_is_full'
  | 'user_has_failed_reverification'
  | 'user_has_plan_hopped'
  | 'user_has_failed_generic_check'
  | 'restricted_country'
  | 'member_removal_page'
  | 'audiobooks_offer_transition'
  | 'gpb_offer_transition';

type ImpressionType = never;

type ActionType = {
  progress_indicator_one: 'continue' | 'dismiss';
  purchase_confirmation: 'set_address';
  user_has_plan_hopped_error: 'see_plans';
  user_has_failed_reverification_error: 'see_plans';
  change_account: 'change' | 'confirm';
  reverification_enter_address: 'save_address' | 'faq';
  reverification_address_confirmed: 'open_spotify';
  reverification_link_expired: 'open_spotify';
  reverification_success: 'open_spotify';
  reverification_failure: 'try_again' | 'see_plans' | 'get_help';
};

function impression(
  pageOrImpression: PageType | ErrorPageType | ImpressionType,
  planType: PlanType,
  properties?: Record<string, string>,
) {
  return createMUPImpression({
    sp_t: Cookie.get('sp_t'),
    plan_type: planType,
    element: pageOrImpression,
    properties,
  });
}

function action<K extends keyof ActionType>(
  pageOrImpression: K,
  actionType: ActionType[K],
  planType: PlanType,
  properties?: Record<string, string>,
) {
  return createMUPAction({
    sp_t: Cookie.get('sp_t'),
    plan_type: planType,
    action: actionType,
    element: pageOrImpression,
    properties,
  });
}

export const progressIndicatorOne = {
  pageView: (planType: PlanType) =>
    impression('progress_indicator_one', planType),
  continue: (planType: PlanType) =>
    action('progress_indicator_one', 'continue', planType),
  dismiss: (planType: PlanType) =>
    action('progress_indicator_one', 'dismiss', planType),
};

type ErrorData = {
  type: string;
  code: string;
};

export const error = {
  pageView: (errorType: ErrorPageType, planType: PlanType, data: ErrorData) =>
    impression(errorType, planType, data),
};

export const purchaseConfirmation = {
  pageView: (planType: PlanType) =>
    impression('purchase_confirmation', planType),
  setAddress: (planType: PlanType) =>
    action('purchase_confirmation', 'set_address', planType),
};

export const userHasPlanHoppedError = {
  seePlans: (planType: PlanType) =>
    action('user_has_plan_hopped_error', 'see_plans', planType),
};

export const userHasFailedReverificationError = {
  seePlans: (planType: PlanType) =>
    action('user_has_failed_reverification_error', 'see_plans', planType),
};

export const changeAccount = {
  pageView: (planType: PlanType, context: ChangeAccountContext) =>
    impression('change_account', planType, { context }),
  change: (planType: PlanType, context: ChangeAccountContext) =>
    action('change_account', 'change', planType, { context }),
  confirm: (planType: PlanType, context: ChangeAccountContext) =>
    action('change_account', 'confirm', planType, { context }),
};

export const reverificationEnterAddress = {
  pageView: (planType: PlanType) =>
    impression('reverification_enter_address', planType),
  saveAddress: (planType: PlanType) =>
    action('reverification_enter_address', 'save_address', planType),
  faq: (planType: PlanType) =>
    action('reverification_enter_address', 'faq', planType),
};

export const reverificationAddressConfirmed = {
  pageView: (planType: PlanType) =>
    impression('reverification_address_confirmed', planType),
  openSpotify: (planType: PlanType) =>
    action('reverification_address_confirmed', 'open_spotify', planType),
};

export const reverificationLinkExpired = {
  pageView: (planType: PlanType) =>
    impression('reverification_link_expired', planType),
  openSpotify: (planType: PlanType) =>
    action('reverification_link_expired', 'open_spotify', planType),
};

export const reverificationSuccess = {
  pageView: (planType: PlanType) =>
    impression('reverification_success', planType),
  openSpotify: (planType: PlanType) =>
    action('reverification_success', 'open_spotify', planType),
};

export const reverificationRedirect = {
  pageView: (planType: PlanType) =>
    impression('reverification_redirect', planType),
};

export const reverificationFailure = {
  pageView: (planType: PlanType) =>
    impression('reverification_failure', planType),
  tryAgain: (planType: PlanType) =>
    action('reverification_failure', 'try_again', planType),
  seePlans: (planType: PlanType) =>
    action('reverification_failure', 'see_plans', planType),
  getHelp: (planType: PlanType) =>
    action('reverification_failure', 'get_help', planType),
};
